import { DATA_UNAVAILABILITY_REASONS } from '@publishing-engine/domain/fixtures';
import {
  AllowedFileFormats,
  FormFieldDefinition,
  ValidationKindReviewers
} from '@publishing-engine/domain/types/article';
import { createMarkDownLink } from '../link';

type PickByComponent<FFD, C> = FFD extends FormFieldDefinition
  ? FFD['component'] extends C
    ? FFD
    : never
  : never;

export type SystemField = PickByComponent<
  FormFieldDefinition,
  `System:${string}`
>;

// export type SystemField = Exclude<
//   PickByComponent<FormFieldDefinition, `System:${string}`>,
//   FormFieldReviewsForAuthor // excluding RevForAuth because it has name: string, messes up type narrowing.
// >;

export type SystemFieldByName<FieldName extends string> = SystemField extends {
  name: infer N;
}
  ? N extends FieldName
    ? SystemField
    : never
  : never;

export const SystemDefaults: {
  [FieldName in SystemField['name']]: SystemFieldByName<FieldName>;
} = {
  ArticleType: {
    name: 'ArticleType',
    component: 'System:ArticleType',
    params: {
      validations: {
        kind: 'options',
        options: [],
        required: true
      },
      labels: {
        main: 'Manuscript Type',
        options: {},
        subtitle: '',
        helpText:
          'Select the manuscript type that best describes your submission'
      }
    },
    caseDataFields: { main: 'type' }
  },
  Title: {
    name: 'Title',
    component: 'System:Title',

    params: {
      validations: {
        kind: 'string',
        required: true
      },
      labels: {
        main: 'Title',
        subtitle:
          'The title should be concise and informative. Please do not use non-standard or obscure abbreviations or acronyms. Maximum 1000 characters.'
      }
    },
    caseDataFields: {
      main: 'metadata.title'
    }
  },
  Subtitle: {
    name: 'Subtitle',
    component: 'System:Subtitle',
    params: {
      validations: {
        kind: 'string',
        required: false
      },
      labels: {
        main: 'Subtitle',
        helpText:
          'Enter the full subtitle in sentence case. Do not use abbreviations or acronyms',
        subtitle:
          'The subtitle should be in article language, concise, informative, and no longer than 280 characters (please do not use non-standard characters or obscure abbreviations or acronyms).'
      }
    },
    caseDataFields: {
      main: 'metadata.subtitle'
    }
  },
  CommentForEditor: {
    name: 'CommentForEditor',
    component: 'System:CommentForEditor',
    params: {
      validations: {
        kind: 'string',
        required: false
      },
      labels: {
        main: 'Comments for the Editor',
        helpText: 'Enter the comments for the Editor',
        subtitle:
          'Add any information that you think our editorial staff should know when evaluating your submission'
      }
    },
    caseDataFields: {
      main: 'preference.commentForEditor'
    }
  },
  Abstract: {
    name: 'Abstract',
    component: 'System:Abstract',
    params: {
      validations: {
        kind: 'string',
        required: true,
        maxLength: 10000
      },
      labels: {
        main: 'Abstract',
        subtitle:
          'Provide a succinct summary of your research that highlights the purpose, methodology, results, and conclusions.'
      }
    },
    caseDataFields: {
      main: 'metadata.abstract'
    }
  },
  ArticleSubtype: {
    name: 'ArticleSubtype',
    component: 'System:ArticleSubtype',
    params: {
      validations: {
        kind: 'string',
        maxLength: 10000,
        required: false
      },
      labels: {
        main: 'Article Subtype',
        helpText: 'Enter the Article Subtype',
        subtitle: 'Research Publication (any others?)'
      }
    },
    caseDataFields: {
      main: 'metadata.articleSubtype'
    }
  },
  Manuscript: {
    name: 'Manuscript',
    component: 'System:Manuscript',

    params: {
      validations: {
        kind: 'file',
        maxSizeMB: 25,
        allowedFileFormats: {} as AllowedFileFormats,
        required: true
      },
      labels: {
        main: 'Manuscript',
        subtitle:
          'Your manuscript should include all figures, tables, and supplementary material necessary for the review process'
      },
      labelsByPerspective: {
        submitter: {
          main: 'Manuscript',
          subtitle:
            'Your manuscript should include all figures, tables, and supplementary material necessary for the review process'
        },
        viewer: {
          main: 'Manuscript',
          subtitle: ''
        }
      }
    },
    caseDataFields: {
      main: 'fileSet.Manuscript'
    }
  },
  Keywords: {
    name: 'Keywords',
    component: 'System:Keywords',

    params: {
      validations: {
        kind: 'stringArray',

        minCountOfKeywords: 1,
        maxCountOfKeywords: 100,
        minLength: 1,
        maxLength: 200,
        required: true
      },
      labels: {
        main: 'Keywords',
        subtitle:
          'List keywords to help index your paper and make it more discoverable to your target audience.'
      }
    },
    caseDataFields: {
      main: 'metadata.keywords'
    }
  },
  SubmittingAuthor: {
    name: 'SubmittingAuthor',
    component: 'System:SubmittingAuthor',

    params: {
      validations: {
        kind: 'authorType',
        required: true
      },
      labels: {
        main: 'Submitting Author',
        subtitle:
          'Focuses on the initial submission process and ensures compliance with journal guidelines.'
      }
    },
    caseDataFields: {
      main: 'submittingAuthor'
    }
  },
  ContactAuthor: {
    name: 'ContactAuthor',
    component: 'System:ContactAuthor',

    params: {
      validations: {
        kind: 'authorType',
        required: true
      },
      labels: {
        main: 'Contact Author',
        subtitle:
          'This Author will manage communications with the journal during the submission and review process.'
      }
    },
    caseDataFields: {
      main: 'contactAuthor'
    }
  },
  CorrespondingAuthor: {
    name: 'CorrespondingAuthor',
    component: 'System:CorrespondingAuthor',

    params: {
      validations: {
        kind: 'authorType',
        required: true
      },
      labels: {
        main: 'Corresponding Author',
        subtitle:
          'Handles all communications post-publication, including reader inquiries and media interactions.'
      }
    },
    caseDataFields: {
      main: 'correspondingAuthor'
    }
  },
  Authors: {
    name: 'Authors',
    component: 'System:Authors',

    params: {
      validations: {
        kind: 'authors',
        required: true,
        orcidField: 'optional',
        affiliationField: 'optional',
        contributionsField: 'optional',
        contributionDegreeField: 'optional'
      },
      labels: {
        main: 'Co-Author Details',
        subtitle:
          'Enter accurate information about each co-author including contact information which they may be contacted by.'
      }
    },
    caseDataFields: {
      main: 'metadata.authors'
    }
  },
  EthicsStatement: {
    name: 'EthicsStatement',
    component: 'System:EthicsStatement',

    params: {
      validations: {
        kind: 'dependantStringField',
        dependantField: 'value',
        maxLength: 10000,
        required: true
      },
      labels: {
        main: 'Field Studies or Experiments with Humans or Animals',
        subtitle:
          'If your research includes Field Studies or experiments with Humans or Animals, then you will be asked to provide an Ethics Statement.',
        options: {
          false: {
            main: 'No, this research does not include Field Studies or experiments with Humans or Animal (including cells, tissues or fluids)'
          },
          true: {
            main: 'Yes, this research included Field Studies or experiments with Humans or Animal (including cells, tissues or fluids)'
          }
        },
        section: {
          title: 'Add your Ethics Statement',
          explanation: `Use the ${createMarkDownLink(
            'Ethics Statement Generator',
            '#'
          )} to draft a statement outlining the ethical approvals, considerations, or exemptions relevant to your research. Once completed, include the statement here.`
        }
      },
      labelsByPerspective: {
        submitter: {
          main: 'Field Studies or Experiments with Humans or Animals',
          subtitle:
            'If your research includes Field Studies or experiments with Humans or Animals, then you will be asked to provide an Ethics Statement.',
          options: {
            false: {
              main: 'No, this research does not include Field Studies or experiments with Humans or Animal (including cells, tissues or fluids)'
            },
            true: {
              main: 'Yes, this research included Field Studies or experiments with Humans or Animal (including cells, tissues or fluids)'
            }
          },
          section: {
            title: 'Add your Ethics Statement',
            explanation: `Use the ${createMarkDownLink(
              'Ethics Statement Generator',
              '#'
            )} to draft a statement outlining the ethical approvals, considerations, or exemptions relevant to your research. Once completed, include the statement here.`
          }
        },
        viewer: {
          main: 'Field Studies or Experiments with Humans or Animals',
          subtitle: '',
          options: {
            false: {
              main: 'No, this research does not include Field Studies or experiments with Humans or Animal (including cells, tissues or fluids)'
            },
            true: {
              main: 'Yes, this research included Field Studies or experiments with Humans or Animal (including cells, tissues or fluids)'
            }
          },
          section: {
            title: 'Add your Ethics Statement',
            explanation: `Use the ${createMarkDownLink(
              'Ethics Statement Generator',
              '#'
            )} to draft a statement outlining the ethical approvals, considerations, or exemptions relevant to your research. Once completed, include the statement here.`
          }
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'metadata.ethicsStatement'
    }
  },
  NoveltyStatement: {
    name: 'NoveltyStatement',
    component: 'System:NoveltyStatement',

    params: {
      validations: {
        kind: 'nullString',
        maxLength: 10000,
        required: true
      },
      labels: {
        main: 'Novelty and Significance Statement',
        subtitle:
          'Enter a statement to help editors and reviewers assess the novelty and significance of your research.',

        section: {
          title: 'Novelty & Significance',
          explanation:
            'Clearly articulating the novelty and significance of your research helps the editorial team and reviewers understand the unique contributions of your work and its impact on the field. A well-crafted statement can highlight the relevance and potential influence of your research, facilitating a more informed and efficient review.'
        }
      },
      labelsByPerspective: {
        submitter: {
          main: 'Novelty and Significance Statement',
          subtitle:
            'Enter a statement to help editors and reviewers assess the novelty and significance of your research.',

          section: {
            title: 'Novelty & Significance',
            explanation:
              'Clearly articulating the novelty and significance of your research helps the editorial team and reviewers understand the unique contributions of your work and its impact on the field. A well-crafted statement can highlight the relevance and potential influence of your research, facilitating a more informed and efficient review.'
          }
        },
        viewer: {
          main: 'Novelty and Significance Statement',
          subtitle: '',

          section: {
            title: 'Novelty & Significance',
            explanation:
              'Clearly articulating the novelty and significance of research helps the editorial team and reviewers understand the unique contributions of the work and its impact on the field. A well-crafted statement can highlight the relevance and potential influence of this research, facilitating a more informed and efficient review.'
          }
        }
      }
    },
    caseDataFields: {
      main: 'metadata.noveltyStatement'
    }
  },
  Funding: {
    name: 'Funding',
    component: 'System:Funding',

    params: {
      validations: {
        kind: 'funding',
        required: false
      },
      labels: {
        main: 'Funding Information',
        subtitle:
          'Enter details of all funding sources that you and your co-authors will be acknowledging in your publication, if applicable. Select the appropriate Funder for each funding source.',

        section: {
          title: 'Funder',
          explanation:
            'Funding details are sourced from the Open Funder Registry, but if your work is funded by institutions or individuals not listed in this registry, please submit them for consideration.'
        }
      },
      labelsByPerspective: {
        submitter: {
          main: 'Funding Information',
          subtitle:
            'Enter details of all funding sources that you and your co-authors will be acknowledging in your publication, if applicable. Select the appropriate Funder for each funding source.',

          section: {
            title: 'Is there Funding to report for this Submission?',
            explanation:
              'Including details about your research funders and associated grants or awards helps the editorial team assess your manuscript’s eligibility for transformative agreements. This information can streamline the review process and ensure compliance with funding requirements.'
          }
        },
        viewer: {
          main: 'Funding Information',
          subtitle: '',

          section: {
            title: 'Is there Funding to report for this Submission?',
            explanation:
              'Including details about this research funders and associated grants or awards helps the editorial team assess the manuscript’s eligibility for transformative agreements. This information can streamline the review process and ensure compliance with funding requirements.'
          }
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'metadata.funding'
    }
  },
  FundingGeneric: {
    name: 'FundingGeneric',
    component: 'System:FundingGeneric',
    params: {
      validations: {
        kind: 'fundingGeneric',
        required: true
      },
      labels: {
        main: 'Funding Information',
        subtitle:
          'Enter details of all funding sources that you and your co-authors will be acknowledging in your publication, if applicable. Select the appropriate Funder for each funding source.',
        options: {},
        section: {
          title: 'Funder',
          explanation:
            'Funding details are sourced from the Open Funder Registry, but if your work is funded by institutions or individuals not listed in this registry, please submit them for consideration.'
        }
      },
      labelsByPerspective: {
        submitter: {
          main: 'Funding Information',
          subtitle:
            'Enter details of all funding sources that you and your co-authors will be acknowledging in your publication, if applicable. Select the appropriate Funder for each funding source.',
          options: {},
          section: {
            title: 'Is there Funding to report for this Submission?',
            explanation:
              "Including details about your research funders and associated grants or awards helps the editorial team assess your manuscript's eligibility for transformative agreements. This information can streamline the review process and ensure compliance with funding requirements."
          }
        },
        viewer: {
          main: 'Funding Information',
          subtitle: '',
          options: {},
          section: {
            title: 'Is there Funding to report for this Submission?',
            explanation:
              "Including details about this research funders and associated grants or awards helps the editorial team assess the manuscript's eligibility for transformative agreements. This information can streamline the review process and ensure compliance with funding requirements."
          }
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'metadata.fundingGeneric'
    }
  },
  FiguresAndTables: {
    name: 'FiguresAndTables',
    component: 'System:FiguresAndTables',

    params: {
      isSourceFileEnabled: false,
      isPermissionEvidenceEnabled: false,
      validations: {
        kind: 'figuresAndTables',
        sourceFile: {
          kind: 'file',
          maxSizeMB: 25,
          allowedFileFormats: {
            'application/pdf': ['.pdf'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats': ['.docx']
          },
          required: false
        },
        permissionEvidence: {
          kind: 'file',
          maxSizeMB: 25,
          allowedFileFormats: {
            'application/pdf': ['.pdf'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats': ['.docx']
          },
          required: false
        },
        required: false
      },
      labels: {
        main: 'Figures and Tables',
        subtitle:
          'Provide your figures and tables, along with any necessary permissions to reproduce them.',
        section: {
          title: 'Does your submission contain figures and tables?',
          explanation:
            'Provide your figures and tables, along with any necessary permissions to reproduce them.'
        }
      },
      labelsByPerspective: {
        submitter: {
          main: 'Figures and Tables',
          subtitle:
            'Provide your figures and tables, along with any necessary permissions to reproduce them.',
          section: {
            title: 'Does your submission contain figures and tables?',
            explanation:
              'Provide your figures and tables, along with any necessary permissions to reproduce them.'
          }
        },
        viewer: {
          main: 'Figures and Tables',
          subtitle: '',
          section: {
            title: 'Does submission contain figures and tables?',
            explanation:
              'Provide figures and tables, along with any necessary permissions to reproduce them.'
          }
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'fileSet.FiguresAndTables'
    }
  },
  PreferenceImmediatePublication: {
    name: 'PreferenceImmediatePublication',
    component: 'System:PreferenceImmediatePublication',

    params: {
      validations: {
        kind: 'bool',
        required: true
      },
      labels: {
        main: 'Immediate Publication Consent',
        subtitle:
          'Opting for immediate publication accelerates the visibility of your work, allowing your research to reach the community swiftly after acceptance.',
        options: {
          true: {
            main: 'Yes, I consent to immediate publication upon acceptance.',
            secondary:
              'This option is ideal for authors eager to disseminate their findings without delay.'
          },
          false: {
            main: 'No, I do not consent to immediate publication upon acceptance.',
            secondary:
              'If you have coordination needs with your institution or require a specific publication timing, you might prefer to withhold immediate consent.'
          }
        }
      },
      labelsByPerspective: {
        submitter: {
          main: 'Immediate Publication Consent',
          subtitle:
            'Opting for immediate publication accelerates the visibility of your work, allowing your research to reach the community swiftly after acceptance.',
          options: {
            true: {
              main: 'Yes, I consent to immediate publication upon acceptance.',
              secondary:
                'This option is ideal for authors eager to disseminate their findings without delay.'
            },
            false: {
              main: 'No, I do not consent to immediate publication upon acceptance.',
              secondary:
                'If you have coordination needs with your institution or require a specific publication timing, you might prefer to withhold immediate consent.'
            }
          }
        },
        viewer: {
          main: 'Immediate Publication Consent',
          subtitle:
            'Selecting this option will make the submission publicly visible shortly after acceptance.',
          options: {
            true: {
              main: 'The Author consents to immediate Publication upon acceptance.',
              secondary: ''
            },
            false: {
              main: 'The Author does not consent to immediate Publication upon acceptance.',
              secondary: ''
            }
          }
        }
      }
    },
    caseDataFields: {
      main: 'preference.immediatePublication'
    }
  },
  StandardReportingGuidelines: {
    name: 'StandardReportingGuidelines',
    component: 'System:StandardReportingGuidelines',

    params: {
      validations: {
        kind: 'bool',
        required: false
      },
      labels: {
        main: 'Standard Reporting Guidelines',
        subtitle: `Did you follow the standard reporting guidelines appropriate to your study?`,
        options: {
          true: {
            main: 'Yes, I confirm I have used the correct guidelines for my study.',
            secondary: ``
          },
          false: {
            main: 'No, the guidelines are not applicable to my study.',
            secondary: ''
          }
        }
      }
    },
    caseDataFields: {
      main: 'preference.standardReportingGuidelines'
    }
  },
  TransparentPeerReview: {
    name: 'TransparentPeerReview',
    component: 'System:TransparentPeerReview',

    params: {
      validations: {
        kind: 'bool',
        required: true
      },
      labels: {
        main: 'Transparent Peer Review',
        subtitle:
          "Reviewer recommendations, author responses, and editor's decision will be published alongside my article, should it be accepted.",
        options: {
          true: {
            main: 'Yes, I agree to the publication of the peer review reports and editorial correspondence if my article is accepted.',
            secondary: ''
          },
          false: {
            main: 'No, I prefer communications remain confidential and unpublished.',
            secondary: ''
          }
        }
      },
      labelsByPerspective: {
        submitter: {
          main: 'Transparent Peer Review',
          subtitle:
            "Reviewer recommendations, author responses, and editor's decision will be published alongside my article, should it be accepted.",
          options: {
            true: {
              main: 'Yes, I agree to the publication of the peer review reports and editorial correspondence if my article is accepted.',
              secondary: ''
            },
            false: {
              main: 'No, I prefer communications remain confidential and unpublished.',
              secondary: ''
            }
          }
        },
        viewer: {
          main: 'Peer Review Model',
          subtitle:
            "If enabled, reviewer recommendations, author responses, and the editor's decision will be published alongside the article upon acceptance.",
          options: {
            true: {
              main: 'The Author consents to a Transparent Peer Review.',
              secondary: ''
            },
            false: {
              main: 'The Author does not consent to a Transparent Peer Review.',
              secondary: ''
            }
          }
        }
      }
    },
    caseDataFields: {
      main: 'preference.transparentPeerReview'
    }
  },
  DataAvailability: {
    name: 'DataAvailability',
    component: 'System:DataAvailability',

    params: {
      validations: {
        kind: 'dataAvailabilityPreference' as any,
        required: true
      },
      labels: {
        main: 'Is your research data publicly available?',
        subtitle:
          'Streamline the publishing process and protect yourself from copyright infringement by identifying reproduced material in your submission',
        options: {
          'openly-available': {
            main: 'The data that support the findings of this study are openly available'
          },
          'embargo-period': {
            main: 'The data that support the findings of this study will be openly available following an embargo'
          },
          'included-in-article': {
            main: 'All data that support the findings of this study are included within the article (and any supplementary files)'
          },
          'not-available': {
            main: 'No, the data supporting the findings of this study are not publicly available.'
          }
        },
        dependentOptions: DATA_UNAVAILABILITY_REASONS
      },
      labelsByPerspective: {
        submitter: {
          main: 'Is your research data publicly available?',
          subtitle:
            'Streamline the publishing process and protect yourself from copyright infringement by identifying reproduced material in your submission',
          options: {
            'openly-available': {
              main: 'The data that support the findings of this study are openly available'
            },
            'embargo-period': {
              main: 'The data that support the findings of this study will be openly available following an embargo'
            },
            'included-in-article': {
              main: 'All data that support the findings of this study are included within the article (and any supplementary files)'
            },
            'not-available': {
              main: 'No, the data supporting the findings of this study are not publicly available.'
            }
          },
          dependentOptions: DATA_UNAVAILABILITY_REASONS
        },
        viewer: {
          main: 'Data Availability',
          subtitle:
            'Helps streamline the publishing process and ensures protection against copyright infringement by identifying reproduced content within the submission.',
          options: {
            'openly-available': {
              main: 'The data that support the findings of this study are openly available'
            },
            'embargo-period': {
              main: 'The data that support the findings of this study will be openly available following an embargo'
            },
            'included-in-article': {
              main: 'All data that support the findings of this study are included within the article (and any supplementary files)'
            },
            'not-available': {
              main: 'No, the data supporting the findings of this study are not publicly available.'
            }
          },
          dependentOptions: DATA_UNAVAILABILITY_REASONS
        }
      }
    },
    caseDataFields: {
      main: 'preference.dataAvailability'
    }
  },
  PublishingModel: {
    name: 'PublishingModel',
    component: 'System:PublishingModel',

    params: {
      validations: {
        kind: 'publishingPreference',
        required: true
      },
      labels: {
        options: {
          openAccess: {
            main: 'Open Access',
            secondary:
              'Your research will be available to readers at no cost. Publication costs might be charged upon manuscript acceptance, and discounts or waivers can be applied depending on your country, institution or other aspects.'
          },
          subscription: {
            main: 'Subscription',
            secondary:
              'Readers only have access to your research by paying a subscription.'
          }
        },
        main: 'Publishing Model',
        subtitle:
          'Select a fitting publishing model to determine how audiences will access your work.',
        section: {
          title: 'Publishing Model',
          explanation:
            'You have a choice to publish your research under different models. Before your research is published, you may be asked to complete the steps that are required for the model that you choose.Your publishing preference will determine how readers access your research.'
        }
      },
      labelsByPerspective: {
        submitter: {
          options: {
            openAccess: {
              main: 'Open Access',
              secondary:
                'Your research will be available to readers at no cost. Publication costs might be charged upon manuscript acceptance, and discounts or waivers can be applied depending on your country, institution or other aspects.'
            },
            subscription: {
              main: 'Subscription',
              secondary:
                'Readers only have access to your research by paying a subscription.'
            }
          },
          main: 'Publishing Model',
          subtitle:
            'Select a fitting publishing model to determine how audiences will access your work.',
          section: {
            title: 'Publishing Model',
            explanation:
              'You have a choice to publish your research under different models. Before your research is published, you may be asked to complete the steps that are required for the model that you choose.Your publishing preference will determine how readers access your research.'
          }
        },
        viewer: {
          options: {
            openAccess: {
              main: 'Open Access',
              secondary: ''
            },
            subscription: {
              main: 'Subscription',
              secondary: ''
            }
          },
          main: 'Publishing Model',
          subtitle: '',
          section: {
            title: 'Publishing Model',
            explanation: ''
          }
        }
      }
    },
    caseDataFields: {
      main: 'preference.publishingModel',
      dependant: 'acknowledgeCharge'
    }
  },
  DoubleAnonymousPeerReview: {
    name: 'DoubleAnonymousPeerReview',
    component: 'System:DoubleAnonymousPeerReview',

    params: {
      validations: {
        kind: 'bool',
        required: true
      },
      labels: {
        main: 'Do you consent to a Double Anonymous Peer Review process?',
        subtitle:
          "Double Anonymous Peer Review ensures that both the authors and reviewers remain unknown to each other, which is designed to remove any potential bias based on the authors' identity, affiliation, or previous work.",
        options: {
          true: {
            main: 'Yes, I prefer a review process that conceals identities to minimize bias.',
            secondary: ''
          },
          false: {
            main: 'No, I am comfortable with my identity being known to the reviewers.',
            secondary: ''
          }
        }
      },
      labelsByPerspective: {
        submitter: {
          main: 'Do you consent to a Double Anonymous Peer Review process?',
          subtitle:
            "Double Anonymous Peer Review ensures that both the authors and reviewers remain unknown to each other, which is designed to remove any potential bias based on the authors' identity, affiliation, or previous work.",
          options: {
            true: {
              main: 'Yes, I prefer a review process that conceals identities to minimize bias.',
              secondary: ''
            },
            false: {
              main: 'No, I am comfortable with my identity being known to the reviewers.',
              secondary: ''
            }
          }
        },
        viewer: {
          main: 'Peer Review Type',
          subtitle:
            'If enabled, ensures anonymity between authors and reviewers to help eliminate bias related to identity, affiliation, or prior publications.',
          options: {
            true: {
              main: 'Double Anonymous',
              secondary: ''
            },
            false: {
              main: 'Single Anonymous',
              secondary: ''
            }
          }
        }
      }
    },
    caseDataFields: {
      main: 'preference.doubleAnonymousPeerReview'
    }
  },
  Figures: {
    name: 'Figures',
    component: 'System:Figures',

    params: {
      validations: {
        kind: 'customArray',
        required: false,
        items: {}
      },
      labels: {
        main: 'Figures',
        subtitle: ''
      },
      labelsByPerspective: {
        submitter: {
          main: 'Figures',
          subtitle: ''
        },
        viewer: {
          main: 'Figures',
          subtitle: ''
        }
      }
    },
    caseDataFields: {
      main: 'fileSet.Figures'
    }
  },
  Tables: {
    name: 'Tables',
    component: 'System:Tables',
    params: {
      validations: {
        kind: 'customArray',
        required: false,
        maxSizeMB: 50,
        allowedFileFormats: {
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
            '.xlsx'
          ],
          'application/msword': ['.doc'],
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            ['.docx'],
          'application/vnd.ms-excel': ['.xls'],
          'text/plain': ['.txt'],
          'application/pdf': ['.pdf'],
          'text/csv': ['.csv']
        } as any,
        maxItems: 10,
        items: {
          type: 'object',
          properties: {
            caption: {
              type: ['string'],
              maxLength: 1000,
              minLength: 1,
              pattern: '^(?!\\s*(?:[Tt][Aa][Bb](?:[Ll][Ee])?[Ss]?)\\s*\\d).+$'
            },
            file: {
              type: 'object',
              properties: {
                name: { type: 'string', minLength: 1 },
                extension: {
                  type: 'string',
                  enum: ['doc', 'docx', 'txt', 'pdf', 'csv', 'xls', 'xlsx']
                },
                referenceName: { type: 'string', minLength: 1 },
                url: { type: 'string', minLength: 5 }
              },
              required: ['name', 'extension', 'referenceName', 'url']
            }
          },
          required: ['file', 'caption']
        }
      },
      labels: {
        main: 'Tables',
        helpText:
          "Accepted formats: DOC/DOCX/TXT/PDF/CSV/XLS/XLSX (Max 50MB). Name files using the table number only; do not use the 'Table' or 'Tab' prefix followed by a numeric identifier. Max 10 tables allowed",
        subtitle: ''
      },
      labelsByPerspective: {
        submitter: {
          main: 'Tables',
          subtitle: ''
        },
        viewer: {
          main: 'Tables',
          subtitle: ''
        }
      }
    },
    caseDataFields: {
      main: 'fileSet.Tables'
    }
  },
  CompleteDocumentForReview: {
    name: 'CompleteDocumentForReview',
    component: 'System:CompleteDocumentForReview',

    params: {
      validations: {
        kind: 'file',
        maxSizeMB: 25,
        allowedFileFormats: {
          'application/pdf': ['.pdf']
        },
        required: false
      },
      labels: {
        main: 'Complete Document for Review',
        subtitle:
          'Please provide a PDF of the complete revised manuscript with highlighted/tracked changes.'
      },
      labelsByPerspective: {
        submitter: {
          main: 'Complete Document for Review',
          subtitle:
            'Please provide a PDF of the complete revised manuscript with highlighted/tracked changes.'
        },
        viewer: {
          main: 'Complete Document for Review',
          subtitle: ''
        }
      }
    },
    caseDataFields: {
      main: 'fileSet.CompleteDocumentForReview'
    }
  },
  AnonymizedManuscript: {
    name: 'AnonymizedManuscript',
    component: 'System:AnonymizedManuscript',

    params: {
      validations: {
        kind: 'file',

        maxSizeMB: 50,
        allowedFileFormats: {
          'application/pdf': ['.pdf']
        },
        required: false
      },
      labels: {
        main: 'Anonymised Manuscript',
        subtitle:
          'Please provide a PDF with a clean version of your document. You should anonymise the file by removing all author information.'
      },
      labelsByPerspective: {
        submitter: {
          main: 'Anonymised Manuscript',
          subtitle:
            'Please provide a PDF with a clean version of your document. You should anonymise the file by removing all author information.'
        },
        viewer: {
          main: 'Anonymised Manuscript',
          subtitle: ''
        }
      }
    },
    caseDataFields: {
      main: 'fileSet.AnonymizedManuscript'
    }
  },
  Supplementary: {
    name: 'Supplementary',
    component: 'System:Supplementary',

    params: {
      validations: {
        kind: 'fileArray',
        maxSizeMB: 25,
        allowedFileFormats: {
          'application/pdf': ['.pdf'],
          'application/postscript': ['.eps'],
          'application/msword': ['.doc'],
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            ['.docx'],
          'application/vnd.openxmlformats-officedocument.presentationml.presentation':
            ['.pptx'],
          'application/vnd.ms-powerpoint': ['.ppt'],
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
            '.xlsx'
          ],
          'application/vnd.ms-excel': ['.xls'],
          'application/vnd.oasis.opendocument.text': ['.odt'],
          'application/rtf': ['.rtf'],
          'application/x-rtf': ['.rtx'],
          'image/png': ['.png'],
          'image/jpeg': ['.jpg', '.jpeg'],
          'image/tiff': ['.tif', '.tiff'],
          'image/bmp': ['.bmp'],
          'image/gif': ['.gif'],
          'image/x-emf': ['.emf'],
          'image/vnd.ms-modi': ['.mdi'],
          'application/xml': ['.xml'],
          'text/html': ['.html'],
          'application/x-tex': ['.tex'],
          'text/x-bibtex': ['.bib'],
          'application/x-latex': [
            '.tex',
            '.latex',
            '.sty',
            '.clo',
            '.cls',
            '.bst',
            '.dtx',
            '.ldf'
          ],
          'application/zip': ['.zip'],
          'application/x-rar-compressed': ['.rar'],
          'application/x-tar': ['.tar'],
          'text/plain': [
            '.txt',
            '.log',
            '.bbl',
            '.aux',
            '.out',
            '.blg',
            '.bak',
            '.fls',
            '.loc',
            '.lof',
            '.nlo',
            '.soc',
            '.thm',
            '.ins',
            '.ini'
          ],
          'text/x-tex': ['.ctex'],
          'video/mp4': ['.mp4'],
          'video/mpeg': ['.mpg'],
          'video/webm': ['.webm'],
          'video/x-msvideo': ['.avi'],
          'video/quicktime': ['.mov'],
          'video/x-ms-wmv': ['.wmv'],
          'application/octet-stream': [
            '.bin',
            '.raw',
            '.inp',
            '.sav',
            '.spl',
            '.cpi',
            '.mat',
            '.opj',
            '.vsd',
            '.dvi'
          ],
          'text/csv': ['.csv'],
          'application/x-bibtex': ['.bbx', '.brf', '.cbx'],
          'chemical/x-cif': ['.cif'],
          'application/x-dbase': ['.dbx'],
          'application/x-mathematica': ['.mtd'],
          'application/x-spss-sav': ['.sav']
        } as any,
        required: false
      },
      labels: {
        main: 'Supplementary Files',
        subtitle:
          'Provide additional context for your research by uploading supplementary data. This data can include extra datasets, code, multimedia files (excluding music), and other relevant materials that enhance your main submission.'
      },
      labelsByPerspective: {
        submitter: {
          main: 'Supplementary Files',
          subtitle:
            'Provide additional context for your research by uploading supplementary data. This data can include extra datasets, code, multimedia files (excluding music), and other relevant materials that enhance your main submission.'
        },
        viewer: {
          main: 'Supplementary Files',
          subtitle: ''
        }
      }
    },
    caseDataFields: {
      main: 'fileSet.Supplementary'
    }
  },
  AdditionalSupplementary: {
    name: 'AdditionalSupplementary',
    component: 'System:AdditionalSupplementary',

    params: {
      validations: {
        kind: 'fileArray',
        maxSizeMB: 25,
        allowedFileFormats: {
          'application/pdf': ['.pdf'],
          'application/postscript': ['.eps'],
          'application/msword': ['.doc'],
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            ['.docx'],
          'application/vnd.openxmlformats-officedocument.presentationml.presentation':
            ['.pptx'],
          'application/vnd.ms-powerpoint': ['.ppt'],
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
            '.xlsx'
          ],
          'application/vnd.ms-excel': ['.xls'],
          'application/vnd.oasis.opendocument.text': ['.odt'],
          'application/rtf': ['.rtf'],
          'application/x-rtf': ['.rtx'],
          'image/png': ['.png'],
          'image/jpeg': ['.jpg', '.jpeg'],
          'image/tiff': ['.tif', '.tiff'],
          'image/bmp': ['.bmp'],
          'image/gif': ['.gif'],
          'image/x-emf': ['.emf'],
          'image/vnd.ms-modi': ['.mdi'],
          'application/xml': ['.xml'],
          'text/html': ['.html'],
          'application/x-tex': ['.tex'],
          'text/x-bibtex': ['.bib'],
          'application/x-latex': [
            '.tex',
            '.latex',
            '.sty',
            '.clo',
            '.cls',
            '.bst',
            '.dtx',
            '.ldf'
          ],
          'application/zip': ['.zip'],
          'application/x-rar-compressed': ['.rar'],
          'application/x-tar': ['.tar'],
          'text/plain': [
            '.txt',
            '.log',
            '.bbl',
            '.aux',
            '.out',
            '.blg',
            '.bak',
            '.fls',
            '.loc',
            '.lof',
            '.nlo',
            '.soc',
            '.thm',
            '.ins',
            '.ini'
          ],
          'text/x-tex': ['.ctex'],
          'video/mp4': ['.mp4'],
          'video/mpeg': ['.mpg'],
          'video/webm': ['.webm'],
          'video/x-msvideo': ['.avi'],
          'video/quicktime': ['.mov'],
          'video/x-ms-wmv': ['.wmv'],
          'application/octet-stream': [
            '.bin',
            '.raw',
            '.inp',
            '.sav',
            '.spl',
            '.cpi',
            '.mat',
            '.opj',
            '.vsd',
            '.dvi'
          ],
          'text/csv': ['.csv'],
          'application/x-bibtex': ['.bbx', '.brf', '.cbx'],
          'chemical/x-cif': ['.cif'],
          'application/x-dbase': ['.dbx'],
          'application/x-mathematica': ['.mtd'],
          'application/x-spss-sav': ['.sav']
        } as any,
        required: false
      },
      labels: {
        main: 'Supporting Information (Supplemental File NOT for Review)',
        subtitle:
          'The files uploaded in this section do not necessarily fall under the purview of the peer review process.'
      },
      labelsByPerspective: {
        submitter: {
          main: 'Supporting Information (Supplemental File NOT for Review)',
          subtitle:
            'The files uploaded in this section do not necessarily fall under the purview of the peer review process.'
        },
        viewer: {
          main: 'Supporting Information (Supplemental File NOT for Review)',
          subtitle: ''
        }
      }
    },
    caseDataFields: {
      main: 'fileSet.AdditionalSupplementary'
    }
  },
  CoverLetter: {
    name: 'CoverLetter',
    component: 'System:CoverLetter',

    params: {
      validations: {
        kind: 'file',

        maxSizeMB: 50,
        allowedFileFormats: {
          'application/pdf': ['.pdf'],
          'application/msword': ['.doc'],
          'application/vnd.openxmlformats': ['.docx']
        },
        required: false
      },
      labels: {
        main: 'Cover Letter',
        subtitle:
          "Enhance your submission by providing a concise cover letter that outlines your work's significance and context, aiding the editor in assessing its importance. Ensure your cover letter includes a statement of purpose, a summary of your findings, and any additional statements required by the journal."
      },
      labelsByPerspective: {
        submitter: {
          main: 'Cover Letter',
          subtitle:
            "Enhance your submission by providing a concise cover letter that outlines your work's significance and context, aiding the editor in assessing its importance. Ensure your cover letter includes a statement of purpose, a summary of your findings, and any additional statements required by the journal."
        },
        viewer: {
          main: 'Cover Letter',
          subtitle: ''
        }
      }
    },
    caseDataFields: {
      main: 'fileSet.CoverLetter'
    }
  },
  RecommendedReviewers: {
    name: 'RecommendedReviewers',
    component: 'System:RecommendedReviewers',

    params: {
      validations: {
        kind: 'reviewers',
        minItems: 0,
        required: false,
        maxItems: 5
      } as ValidationKindReviewers,
      labels: {
        main: 'Would you like to recommend specific reviewers for this Submission?',
        subtitle:
          'If your expertise helps identify the best reviewers for your work, please specify which Reviewers should be included in the process, to ensure a Peer Review suited to your work.',
        options: {
          true: {
            main: 'Yes'
          },
          false: {
            main: 'No'
          }
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'preference.recommendedReviewers'
    }
  },
  OpposedReviewers: {
    name: 'OpposedReviewers',
    component: 'System:OpposedReviewers',

    params: {
      validations: {
        kind: 'reviewers',
        minItems: 0,
        required: false,
        maxItems: 5
      } as ValidationKindReviewers,
      labels: {
        main: 'Would you like to oppose specific reviewers from this submission?',
        subtitle:
          'If there are any reviewers who may have a conflict of interest or other reasons not suitable for reviewing your work, please specify which reviewers should be excluded from the process to ensure an unbiased peer review.',
        options: {
          true: {
            main: 'Yes'
          },
          false: {
            main: 'No'
          }
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'preference.opposedReviewers'
    }
  },
  ClinicalTrials: {
    name: 'ClinicalTrials',
    component: 'System:ClinicalTrials',
    params: {
      validations: {
        kind: 'dependantStringField',
        dependantField: 'value',
        required: true
      },
      labels: {
        main: 'Clinical Trials',
        subtitle:
          'If your research includes any data from a Clinical Trial, then you will be asked to provide its trial registration number.',
        options: {
          false: {
            main: 'No'
          },
          true: {
            main: 'Yes'
          }
        },
        section: {
          title: 'Clinical Trial Registration Number',
          explanation: 'Please provide the clinical trial registration number.'
        }
      },
      labelsByPerspective: {
        submitter: {
          main: 'Clinical Trials',
          subtitle:
            'If your research includes any data from a Clinical Trial, then you will be asked to provide its trial registration number.',
          options: {
            false: {
              main: 'No'
            },
            true: {
              main: 'Yes'
            }
          },
          section: {
            title: 'Clinical Trial Registration Number',
            explanation:
              'Please provide the clinical trial registration number.'
          }
        },
        viewer: {
          main: 'Clinical Trials',
          subtitle: '',
          options: {
            false: {
              main: 'No'
            },
            true: {
              main: 'Yes'
            }
          },
          section: {
            title: 'Clinical Trial Registration Number',
            explanation:
              'Please provide the clinical trial registration number.'
          }
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'metadata.clinicalTrials'
    }
  },
  AcceleratedPublication: {
    name: 'AcceleratedPublication',
    component: 'System:AcceleratedPublication',

    params: {
      validations: {
        kind: 'nullString',
        maxLength: 10000,
        required: false
      },
      labels: {
        main: 'Accelerated Publication Justification',
        subtitle:
          'Let us know the reasons as to why this Letter should undergo Accelerated Publication.',
        section: {
          title: '',
          explanation: ''
        }
      },
      labelsByPerspective: {
        submitter: {
          main: 'Accelerated Publication Justification',
          subtitle:
            'Let us know the reasons as to why this Letter should undergo Accelerated Publication.',
          section: {
            title: '',
            explanation: ''
          }
        },
        viewer: {
          main: 'Accelerated Publication Justification',
          subtitle: '',
          section: {
            title: '',
            explanation: ''
          }
        }
      }
    },
    caseDataFields: {
      main: 'preference.acceleratedPublication'
    }
  },
  IPEMMembership: {
    name: 'IPEMMembership',
    component: 'System:IPEMMembership',

    params: {
      validations: {
        kind: 'nullString',
        maxLength: 10000,
        required: false
      },
      labels: {
        main: 'Institute of Physics and Engineering in Medicine (IPEM) Membership',
        subtitle:
          'To claim an IPEM Membership discount of 15%, enter your IPEM Membership number.',
        section: {
          title: '',
          explanation: ''
        }
      },
      labelsByPerspective: {
        submitter: {
          main: 'Institute of Physics and Engineering in Medicine (IPEM) Membership',
          subtitle:
            'To claim an IPEM Membership discount of 15%, enter your IPEM Membership number.',
          section: {
            title: '',
            explanation: ''
          }
        },
        viewer: {
          main: 'Institute of Physics and Engineering in Medicine (IPEM) Membership',
          subtitle: '',
          section: {
            title: '',
            explanation: ''
          }
        }
      }
    },
    caseDataFields: {
      main: 'preference.ipemMembership'
    }
  },
  ReviewerComments: {
    name: 'ReviewerComments',
    component: 'System:ReviewsForAuthor',

    params: {
      validations: {
        kind: 'reviewsForAuthor',
        required: false
      },
      labels: {
        main: 'Reviewer Revision Notes',
        subtitle: 'Elaborate on the changes requested from the author.'
      }
    },
    caseDataFields: {
      main: 'reviews'
    }
  },
  // ManuscriptWithTrackedChanges: {
  //   // @deprecated - remove once confirmed. See https://morressier.atlassian.net/browse/PENG-755
  //   name: 'ManuscriptWithTrackedChanges',
  //   component: 'System:ManuscriptWithTrackedChanges',

  //   params: {
  //     validations: {
  //       kind: 'file',
  //       maxSizeMB: 25,
  //       allowedFileFormats: {
  //         'application/pdf': ['.pdf'],
  //         'application/msword': ['.doc'],
  //         'application/vnd.openxmlformats': ['.docx']
  //       },
  //       required: true
  //     },
  //     labels: {
  //       main: 'Update manuscript',
  //       subtitle:
  //         'Provide a non-anonymised version with tracked changes for Peer Review.'
  //     }
  //   },
  //   caseDataFields: {
  //     main: 'fileSet.ManuscriptWithTrackedChanges'
  //   }
  // },
  // AnonymizedManuscriptWithTrackedChanges: {
  //   // @deprecated - remove once confirmed. See https://morressier.atlassian.net/browse/PENG-755
  //   name: 'AnonymizedManuscriptWithTrackedChanges',
  //   component: 'System:AnonymizedManuscriptWithTrackedChanges',

  //   params: {
  //     validations: {
  //       kind: 'file',
  //       maxSizeMB: 25,
  //       allowedFileFormats: {
  //         'application/pdf': ['.pdf'],
  //         'application/msword': ['.doc'],
  //         'application/vnd.openxmlformats': ['.docx']
  //       },
  //       required: false
  //     },
  //     labels: {
  //       main: 'Update anonymised manuscript',
  //       subtitle:
  //         'Provide an anonymised version with tracked changes for Double Anonymous Peer Review.'
  //     }
  //   },
  //   caseDataFields: {
  //     main: 'fileSet.AnonymizedManuscriptWithTrackedChanges'
  //   }
  // },
  SourceFiles: {
    name: 'SourceFiles',
    component: 'System:SourceFiles',

    params: {
      validations: {
        kind: 'fileArray',
        maxSizeMB: 50,
        allowedFileFormats: {
          'application/msword': ['.doc'],
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            ['.docx'],
          'application/x-latex': ['.tex', '.latex'],
          'image/png': ['.png'],
          'application/postscript': ['.eps'],
          'image/jpeg': ['.jpg', '.jpeg']
        } as any,
        required: false
      },
      labels: {
        main: 'Source Files',
        subtitle:
          'You may upload your manuscript source files to assist with processing. Accepted formats are Word (.doc/.docx) or LaTeX (.tex). For LaTeX submissions, first upload the main .tex file. If your manuscript has supporting files, click “Add Supporting .zip” and upload a ZIP containing the main .tex file together with all supporting documents. Figures may also be uploaded in standard formats (PNG, JPG, EPS).'
      },
      labelsByPerspective: {
        submitter: {
          main: 'Source Files',
          subtitle:
            'Please provide clean source files for the revised manuscript with no tracked changes or comments. These files should not be anonymized. Accepted formats include Word, TeX/LaTeX, PNG, JPG, or EPS. Make sure to include all author information, funding information, ethical statements, and acknowledgements within the source files.'
        },
        viewer: {
          main: 'Source Files',
          subtitle: ''
        }
      }
    },
    caseDataFields: {
      main: 'fileSet.SourceFiles'
    }
  },
  Copyright: {
    name: 'Copyright',
    component: 'System:Copyright',

    params: {
      validations: {
        kind: 'file',
        maxSizeMB: 25,
        allowedFileFormats: {
          'application/pdf': ['.pdf'],
          'application/msword': ['.doc'],
          'application/vnd.openxmlformats': ['.docx']
        },
        required: true
      },
      labels: {
        main: 'License Agreement',
        subtitle:
          'We need you to complete a license agreement so that we may publish your article.'
      },
      labelsByPerspective: {
        submitter: {
          main: 'License Agreement',
          subtitle:
            'We need you to complete a license agreement so that we may publish your article.'
        },
        viewer: {
          main: 'License Agreement',
          subtitle: ''
        }
      }
    },
    caseDataFields: {
      main: 'fileSet.Copyright'
    }
  },
  TitlePage: {
    name: 'TitlePage',
    component: 'System:TitlePage',

    params: {
      validations: {
        kind: 'file',
        maxSizeMB: 25,
        allowedFileFormats: {
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            ['.docx']
        },
        required: true
      },
      labels: {
        main: 'Title Page',
        subtitle:
          'Please upload your title page and include the following sections in the Title Page document only: Title, Authors and affiliations (including mail ids and ORCiDs as applicable), Corresponding Author(s), Author(s) Affiliations, Funding statement, Conflicts of Interest, Acknowledgements and Author(s) contributions.'
      },
      labelsByPerspective: {
        submitter: {
          main: 'Title Page',
          subtitle:
            'Please upload your title page and include the following sections in the Title Page document only: Title, Authors and affiliations (including mail ids and ORCiDs as applicable), Corresponding Author(s), Author(s) Affiliations, Funding statement, Conflicts of Interest, Acknowledgements and Author(s) contributions.'
        },
        viewer: {
          main: 'Title Page',
          subtitle: ''
        }
      }
    },
    caseDataFields: {
      main: 'fileSet.TitlePage'
    }
  },
  ConsolidatedPdf: {
    name: 'ConsolidatedPdf',
    component: 'System:ConsolidatedPdf',

    params: {
      validations: {
        kind: 'file',
        maxSizeMB: 50,
        allowedFileFormats: {
          'application/pdf': ['.pdf']
        },
        required: false
      },
      labels: {
        main: 'Editor View',
        subtitle:
          'Consolidated PDF of the submission files for editorial review.'
      },
      labelsByPerspective: {
        submitter: {
          main: 'Editor View',
          subtitle:
            'Consolidated PDF of the submission files for editorial review.'
        },
        viewer: {
          main: 'Editor View',
          subtitle: ''
        }
      }
    },
    caseDataFields: {
      main: 'fileSet.ConsolidatedPdf'
    }
  },
  AnonymizedConsolidatedPdf: {
    name: 'AnonymizedConsolidatedPdf',
    component: 'System:AnonymizedConsolidatedPdf',

    params: {
      validations: {
        kind: 'file',
        maxSizeMB: 50,
        allowedFileFormats: {
          'application/pdf': ['.pdf']
        },
        required: false
      },
      labels: {
        main: 'Anonymous View',
        subtitle:
          'Anonymous PDF of the submission files with author information removed.'
      },
      labelsByPerspective: {
        submitter: {
          main: 'Anonymous View',
          subtitle:
            'Anonymous PDF of the submission files with author information removed.'
        },
        viewer: {
          main: 'Anonymous View',
          subtitle: ''
        }
      }
    },
    caseDataFields: {
      main: 'fileSet.AnonymizedConsolidatedPdf'
    }
  },
  ConflictOfInterestForAuthor: {
    name: 'ConflictOfInterestForAuthor',
    component: 'System:ConflictOfInterestForAuthor',
    params: {
      validations: {
        kind: 'dependantStringField',
        dependantField: 'value',
        required: true
      },
      labels: {
        main: 'Conflict of Interest',
        subtitle: 'Do you have any conflict of interest?',
        options: {
          false: {
            main: 'No'
          },
          true: {
            main: 'Yes'
          }
        },
        section: {
          title: '',
          explanation: 'If so, please state:'
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'metadata.conflictOfInterestForAuthor'
    }
  },
  PreviouslySubmitted: {
    name: 'PreviouslySubmitted',
    component: 'System:PreviouslySubmitted',
    params: {
      validations: {
        kind: 'dependantStringField',
        dependantField: 'value',
        required: true
      },
      labels: {
        main: 'Previously Submitted',
        subtitle:
          'Has this manuscript been submitted previously to this journal?',
        options: {
          false: {
            main: 'No'
          },
          true: {
            main: 'Yes'
          }
        },
        section: {
          title: '',
          explanation:
            ' If yes, please enter the manuscript ID of your previous submission.'
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'metadata.previouslySubmitted'
    }
  },
  Preprint: {
    name: 'Preprint',
    component: 'System:Preprint',
    params: {
      validations: {
        kind: 'dependantStringField',
        dependantField: 'value',
        required: true,
        pattern:
          '^https?:\\/\\/(www\\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()@:%_\\+.~#?&\\/\\/=]*)$'
      },
      labels: {
        main: 'Preprint',
        subtitle: 'Is this paper available on a preprint server?',
        options: {
          false: {
            main: 'No'
          },
          true: {
            main: 'Yes'
          }
        },
        section: {
          title: '',
          explanation: 'If yes, please provide the preprint link.'
        }
      },
      labelsByPerspective: {
        submitter: {
          main: 'Preprint',
          subtitle: 'Is this paper available on a preprint server?',
          options: {
            false: {
              main: 'No'
            },
            true: {
              main: 'Yes'
            }
          },
          section: {
            title: '',
            explanation: 'If yes, please provide the preprint link.'
          }
        },
        viewer: {
          main: 'Preprint',
          subtitle: 'Is this paper available on a preprint server?',
          options: {
            false: {
              main: 'No'
            },
            true: {
              main: 'Yes'
            }
          },
          section: {
            title: '',
            explanation: 'If yes, please provide the preprint link.'
          }
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'metadata.preprint'
    }
  },
  AITools: {
    name: 'AITools',
    component: 'System:AITools',
    params: {
      validations: {
        kind: 'dependantCheckboxArrayWithText',
        dependantField: 'value',
        textField: 'description',
        required: true
      },
      labels: {
        main: 'AI Tools',
        subtitle:
          'Did you use AI Tools (e.g., ChatGPT) to generate any element (including figures) within your manuscript?',
        options: {
          true: {
            main: 'Yes'
          },
          false: {
            main: 'No'
          }
        },
        dependentOptions: {},
        section: {
          title: '',
          explanation:
            'Please specify the AI tool(s) used. You will need to add an acknowledgement to the paper outlining the use of AI.'
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'metadata.aiTools'
    }
  },
  SharedDataset: {
    name: 'SharedDataset',
    component: 'System:SharedDataset',
    params: {
      validations: {
        kind: 'dependantStringField',
        dependantField: 'value',
        required: true
      },
      labels: {
        main: 'Shared Dataset',
        subtitle:
          'Is this paper a part of a number of papers from the same data set ?',
        options: {
          false: {
            main: 'No'
          },
          true: {
            main: 'Yes'
          }
        },
        section: {
          title: '',
          explanation:
            'If yes, list your related publications and explain how this submission differs from them.'
        }
      },
      labelsByPerspective: {
        submitter: {
          main: 'Shared Dataset',
          subtitle:
            'Is this paper a part of a number of papers from the same data set ?',
          options: {
            false: {
              main: 'No'
            },
            true: {
              main: 'Yes'
            }
          },
          section: {
            title: '',
            explanation:
              'If yes, list your related publications and explain how this submission differs from them.'
          }
        },
        viewer: {
          main: 'Shared Dataset',
          subtitle: '',
          options: {
            false: {
              main: 'No'
            },
            true: {
              main: 'Yes'
            }
          },
          section: {
            title: '',
            explanation:
              'If yes, list your related publications and explain how this submission differs from them.'
          }
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'metadata.sharedDataset'
    }
  },
  GeneratedDataset: {
    name: 'GeneratedDataset',
    component: 'System:GeneratedDataset',
    params: {
      validations: {
        kind: 'bool',
        required: true
      },
      labels: {
        main: 'Generated Dataset',
        subtitle:
          'Did your work generate any major datasets (e.g., DNA sequence data, clinical trial data, field data)?',
        options: {
          false: {
            main: 'No'
          },
          true: {
            main: 'Yes'
          }
        }
      },
      labelsByPerspective: {
        submitter: {
          main: 'Generated Dataset',
          subtitle:
            'Did your work generate any major datasets (e.g., DNA sequence data, clinical trial data, field data)?',
          options: {
            false: {
              main: 'No'
            },
            true: {
              main: 'Yes'
            }
          }
        },
        viewer: {
          main: 'Generated Dataset',
          subtitle:
            'Did your work generate any major datasets (e.g., DNA sequence data, clinical trial data, field data)?',
          options: {
            false: {
              main: 'No'
            },
            true: {
              main: 'Yes'
            }
          }
        }
      }
    },
    caseDataFields: {
      main: 'metadata.generatedDataset'
    }
  },
  UtilizedDataset: {
    name: 'UtilizedDataset',
    component: 'System:UtilizedDataset',
    params: {
      validations: {
        kind: 'bool',
        required: true
      },
      labels: {
        main: 'Utilized Dataset',
        subtitle:
          'Did your work use any previously published major datasets (e.g., DNA sequence data, clinical trial data, field data)?',
        options: {
          false: {
            main: 'No'
          },
          true: {
            main: 'Yes'
          }
        }
      },
      labelsByPerspective: {
        submitter: {
          main: 'Utilized Dataset',
          subtitle:
            'Did your work use any previously published major datasets (e.g., DNA sequence data, clinical trial data, field data)?',
          options: {
            false: {
              main: 'No'
            },
            true: {
              main: 'Yes'
            }
          }
        },
        viewer: {
          main: 'Utilized Dataset',
          subtitle:
            'Did your work use any previously published major datasets (e.g., DNA sequence data, clinical trial data, field data)?',
          options: {
            false: {
              main: 'No'
            },
            true: {
              main: 'Yes'
            }
          }
        }
      }
    },
    caseDataFields: {
      main: 'metadata.utilizedDataset'
    }
  },
  EthicalApproval: {
    name: 'EthicalApproval',
    component: 'System:EthicalApproval',
    params: {
      validations: {
        kind: 'dependantStringField',
        dependantField: 'value',
        required: true
      },
      labels: {
        main: 'Ethical Approval',
        subtitle:
          'Did this project receive ethical approval from a formal institutional review board (IRB)?',
        options: {
          false: {
            main: 'No'
          },
          true: {
            main: 'Yes'
          }
        },
        section: {
          title: '',
          explanation:
            'If yes, please provide name of IRB and ethical approval number:'
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'metadata.ethicalApproval'
    }
  },
  EthicsApproval: {
    name: 'EthicsApproval',
    component: 'System:EthicsApproval',
    params: {
      validations: {
        kind: 'dependantStringField',
        dependantField: 'value',
        required: true
      },
      labels: {
        main: 'Ethics Approval',
        subtitle:
          'Have you explicitly acknowledged this ethics approval in your manuscript, within the methods section?',
        options: {
          false: {
            main: 'Yes',
            secondary: undefined
          },
          true: {
            main: 'No',
            secondary: undefined
          }
        },
        section: {
          title: '',
          explanation:
            'If not, please explain why you have not needed to do so:'
        }
      },
      labelsByPerspective: {
        submitter: {
          main: 'Ethics Approval',
          subtitle:
            'Have you explicitly acknowledged this ethics approval in your manuscript, within the methods section?',
          options: {
            false: {
              main: 'Yes',
              secondary: undefined
            },
            true: {
              main: 'No',
              secondary: undefined
            }
          },
          section: {
            title: '',
            explanation:
              'If not, please explain why you have not needed to do so:'
          }
        },
        viewer: {
          main: 'Ethics Approval',
          subtitle: '',
          options: {
            false: {
              main: 'Yes',
              secondary: undefined
            },
            true: {
              main: 'No',
              secondary: undefined
            }
          },
          section: {
            title: '',
            explanation:
              'If not, please explain why you have not needed to do so:'
          }
        }
      }
    },
    caseDataFields: {
      dependsOn: 'exists',
      main: 'metadata.ethicsApproval'
    }
  },
  GraphicalAbstractImage: {
    name: 'GraphicalAbstractImage',
    component: 'System:GraphicalAbstractImage',

    params: {
      validations: {
        kind: 'customArray',
        required: false,
        items: {}
      },
      labels: {
        main: 'Graphical Abstract',
        subtitle: ''
      },
      labelsByPerspective: {
        submitter: {
          main: 'Graphical Abstract',
          subtitle: ''
        },
        viewer: {
          main: 'Graphical Abstract',
          subtitle: ''
        }
      }
    },
    caseDataFields: {
      main: 'fileSet.GraphicalAbstractImage'
    }
  }
};
